import { useSyncExternalStore } from "react"
import { coreStore, RootState, AppDispatch } from "./store"

/**
 * React hook to read a value from the core store with automatic re-rendering.
 *
 * This is the recommended way to consume core state in React components.
 * It uses React 18's useSyncExternalStore under the hood — tear-free reads,
 * no manual subscriptions, no useEffect boilerplate.
 *
 * Usage:
 *   import { useCoreSelector, selectUserToken } from "@forcepoint/platform-coreui"
 *
 *   function MyComponent() {
 *     const token = useCoreSelector(selectUserToken)
 *     // component re-renders automatically when token changes
 *   }
 *
 * With inline selector:
 *   const appName = useCoreSelector((state) => state.activeApp?.applicationName)
 *
 * IMPORTANT: If using an inline selector, wrap it in useCallback to avoid
 * re-subscribing on every render. Exported selectors (selectUserToken, etc.)
 * are stable module-level references and don't need wrapping.
 */
export function useCoreSelector<T>(selector: (state: RootState) => T): T {
  return useSyncExternalStore(
    coreStore.subscribe,
    () => selector(coreStore.getState())
  )
}

/**
 * React hook that returns the typed core store dispatch function.
 *
 * Usage:
 *   import { useCoreDispatch, coreActions } from "@forcepoint/platform-coreui"
 *
 *   function MyComponent() {
 *     const dispatch = useCoreDispatch()
 *     dispatch(coreActions.setLoaderStatus(true))
 *   }
 */
export function useCoreDispatch(): AppDispatch {
  return coreStore.dispatch
}
